import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Shield, Leaf, Clock, Star, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { products, whyChooseUs, testimonials } from '../mockData';
import SEO from '../components/SEO';

const Home = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Koctech Klima - Doğanın Dengesi, Evinizin Konforu | Ankara"
        description="Koctech Klima - Doğanın Dengesi, Evinizin Konforu. Ankara'nin guvenilir klima uzmani. Klima satis, montaj, bakim ve VRF sistemleri. 20+ yillik deneyim. 0551 638 09 34."
        url="https://koctech-iklimlendirme.com/"
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-blue-50 -z-10" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-teal-200 rounded-full filter blur-3xl opacity-20 -z-10" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-200 rounded-full filter blur-3xl opacity-20 -z-10" />
        
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block">
                <span className="bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold">
                  Profesyonel İklimlendirme Çözümleri
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Konforunuz İçin
                <span className="block text-teal-600">En İyi Çözüm</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                20+ yıllık deneyimimiz ile ev ve işyeriniz için profesyonel klima satış, montaj ve servis hizmeti sunuyoruz.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/ai-recommendation">
                  <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 group">
                    Klima Önerisi Al
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-2 border-teal-500 text-teal-600 hover:bg-teal-50 transition-all duration-300">
                    İletişime Geçin
                  </Button>
                </Link>
              </div>
              <div className="flex items-center space-x-8 pt-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-teal-600">2000+</div>
                  <div className="text-sm text-gray-600">Mutlu Müşteri</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-teal-600">20+</div>
                  <div className="text-sm text-gray-600">Yıllık Deneyim</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-teal-600">7/24</div>
                  <div className="text-sm text-gray-600">Teknik Destek</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img
                  src="https://images.unsplash.com/photo-1761330440311-16e160cad236?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb21mb3J0YWJsZSUyMGxpdmluZyUyMHJvb20lMjBhaXIlMjBjb25kaXRpb25pbmd8ZW58MHx8fHwxNzYzMjA3NTM2fDA&ixlib=rb-4.1.0&q=85"
                  alt="Modern Klima Sistemi"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-72 h-72 bg-gradient-to-br from-teal-400 to-teal-600 rounded-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Neden Koctech Klima?</h2>
            <p className="text-xl text-gray-600">Müşterilerimize en iyi hizmeti sunmak için çalışıyoruz</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item) => {
              const IconComponent = {
                Award,
                Shield,
                Leaf,
                Clock
              }[item.icon];
              return (
                <Card key={item.id} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 group hover:-translate-y-2">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Öne Çıkan Ürünler</h2>
            <p className="text-xl text-gray-600">Enerji tasarruflu ve modern klima sistemleri</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.slice(0, 3).map((product) => (
              <Card key={product.id} className="overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-300 group">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-teal-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {product.energyClass}
                  </div>
                </div>
                <CardContent className="p-6">
                  <span className="text-sm text-teal-600 font-semibold">{product.category}</span>
                  <h3 className="text-xl font-bold text-gray-900 mt-2 mb-2">{product.name}</h3>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-teal-600">{product.btu} BTU</span>
                    <Link to="/products">
                      <Button variant="ghost" className="text-teal-600 hover:text-teal-700 hover:bg-teal-50 group/btn">
                        Detay
                        <ChevronRight className="ml-1 w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-300" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/products">
              <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white shadow-lg hover:shadow-xl transition-all duration-300">
                Tüm Ürünleri Görün
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Müşterilerimiz Ne Diyor?</h2>
            <p className="text-xl text-gray-600">Bize güvenen müşterilerimizin görüşleri</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">"{testimonial.comment}"</p>
                  <div className="flex items-center">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover mr-4"
                    />
                    <div>
                      <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                      <p className="text-sm text-gray-600">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-teal-500 to-teal-600">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Konforlu Bir Yaşam İçin Hemen İletişime Geçin
          </h2>
          <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto">
            Ücretsiz keşif ve danışmanlık hizmeti için bizi arayın
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" variant="secondary" className="bg-white text-teal-600 hover:bg-gray-100 shadow-xl hover:shadow-2xl transition-all duration-300">
                Teklif Alın
              </Button>
            </Link>
            <a href="tel:05516380934">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-teal-600 transition-all duration-300">
                Hemen Arayın: 0551 638 09 34
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;